import { Archive, ArrowLeft, Mail, Pencil, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailColumns } from '@/components/ui/detail-columns';
import type { ApiError } from '@/core/api';
import { AuditTimelinePanel } from '@/features/audit';
import { CommentsPanel } from '@/features/comments';
import {
  ActivitiesPanel,
  ActivityFormDialog,
  useCreateActivity,
  type ActivityInput,
} from '@/features/crm/activities';
import { useCompany } from '@/features/crm/companies/queries';
import { AttachmentsPanel } from '@/features/documents';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { TagsPanel } from '@/features/tags';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { useArchiveContact, useContact, usePurgeContact, useRestoreContact } from '../queries';
import { contactName } from '../types';

export function ContactDetailPage() {
  const { publicId = '' } = useParams();
  const contactId = publicId;
  const navigate = useNavigate();

  const { data: contact, isPending, isError, error } = useContact(contactId);
  const company = useCompany(contact?.company ?? -1);
  const archive = useArchiveContact();
  const restore = useRestoreContact();
  const purge = usePurgeContact();
  const createActivity = useCreateActivity();
  const [loggingEmail, setLoggingEmail] = useState(false);

  function handleSendEmail() {
    if (!contact?.email) return;
    // Launch the user's mail client, then open the log dialog pre-seeded as an
    // email activity so the outreach is captured on the timeline (#14).
    window.open(`mailto:${contact.email}`, '_blank', 'noopener,noreferrer');
    setLoggingEmail(true);
  }

  async function handleLogEmail(input: ActivityInput) {
    await createActivity.mutateAsync(input);
  }

  async function handleArchive() {
    await archive.mutateAsync(contactId);
    navigate('/crm/contacts');
  }

  function handleRestore() {
    restore.mutate(contactId);
  }

  function handlePurge() {
    purge.mutate(contactId, { onSuccess: () => navigate('/crm/contacts') });
  }

  if (isPending) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/contacts')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/contacts')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{contactName(contact)}</h1>
        </div>
        <div className="flex gap-2">
          {contact.email ? (
            <Button variant="outline" onClick={handleSendEmail}>
              <Mail /> Send email
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(`/crm/contacts/${contact.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive contact">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this contact?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide this row from the active list. You can restore it later from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archive.isPending}>
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {contact.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="text-sm">
              <p className="font-medium">This contact is archived.</p>
              <p className="text-muted-foreground">
                Restore it to make it visible in the active list again.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={restore.isPending}
                aria-label="Restore contact"
              >
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={contactName(contact)}
                onPurge={handlePurge}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Email">{contact.email || '—'}</Field>
                <Field label="Phone">{contact.phone_number || '—'}</Field>
                <Field label="Title">{contact.title || '—'}</Field>
                <Field label="Company">{contact.company ? (company.data?.name ?? '…') : '—'}</Field>
                <Field label="Notes" className="sm:col-span-2">
                  {contact.notes || '—'}
                </Field>
              </CardContent>
            </Card>

            <CustomFieldsPanel entity="crm.contact" entityId={contact.id} />

            <AttachmentsPanel modelLabel="crm.contact" objectId={contact.id} />

            <CommentsPanel modelLabel="crm.contact" objectId={contact.id} />
          </>
        }
        aside={
          <>
            <TagsPanel modelLabel="crm.contact" objectId={contact.id} />

            <ActivitiesPanel scope={{ entity: 'contact', entityId: contact.id }} />
            <AuditTimelinePanel entity="crm.contact" entityId={contact.id} />
          </>
        }
      />

      {/* Pre-seeded email activity opened by the "Send email" CTA (#14). */}
      <ActivityFormDialog
        open={loggingEmail}
        onOpenChange={setLoggingEmail}
        defaults={{
          type: 'email',
          subject: `Email to ${contactName(contact)}`,
          contact: contact.id,
          company: contact.company ?? undefined,
        }}
        onSubmit={handleLogEmail}
      />
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <div className="mt-1 text-sm break-words whitespace-pre-wrap">{children}</div>
    </div>
  );
}
